import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../css/bg.css";
import Logo from "../Components/Assets/logo.svg";
import Paper from '../Components/Assets/paper.png';
import segLogo from "../Components/Assets/seg-logo.png";
import Market from "../Components/market/market";
import Background from '../Components/background/background';
import NextBtnLoader from '../Components/loader/loader';
import axios from 'axios';
import config from '../Context/Config';
import { safeRequestIdleCallback } from "../Utils/requestIdleCallbackPolyfill";

const LoginPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [dob, setDob] = useState("");
  const location = useLocation();
  const [error, setError] = useState("");
  const inputRefs = useRef([]);


  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenParam = params.get("token");
    config.token = tokenParam;
    if (!tokenParam) {
      navigate("/context-not-found", { state: { openedViaButton: true } });
    }
    else {
      const verifyToken = async () => {
        try {
          setIsLoading(true);
          const cacheKey = `token_verify_${tokenParam}`;
          const cached = sessionStorage.getItem(cacheKey);

          let response;
          if (cached) {
            response = { data: JSON.parse(cached) };
          } else {
            response = await axios.post(
              `${config.API_BASE_URL}/verify_token`,
              { token: tokenParam }
            );
            sessionStorage.setItem(cacheKey, JSON.stringify(response.data));
          }

          const { is_valid, form_submitted } = response.data;

          if (!is_valid) {
            if (form_submitted) {
              navigate("/form-submission-success", { state: { openedViaLogin: true } });
            }
            else {
              navigate("/unauth", { state: { openedViaButton: false } });
            }
          }
        } catch (error) {
          navigate("/unauth", { state: { openedViaButton: false } });
        }
        finally {
          setIsLoading(false);
        }
      };
      safeRequestIdleCallback(() => verifyToken(), { timeout: 3000 });
    }

  }, [location, navigate]);

  const handleOTPChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);

    if (value && index < otp.length - 1) {
      setTimeout(() => inputRefs.current[index + 1]?.focus(), 0);
    }
  };

  const handleOTPKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      setTimeout(() => inputRefs.current[index - 1]?.focus(), 0);
    }
  };


  const handleOTPPaste = (e) => {
    const paste = e.clipboardData.getData("text");

    if (!/^[0-9]{6}$/.test(paste)) return; // Must be exactly 6 digits

    const updated = paste.split("");
    setOtp(updated);

    // Move focus to last field
    document.getElementById("otp-5").focus();
  };


  const LogoGroup = ({ logos }) => (
    <div className="flex flex-wrap items-center justify-center gap-4 mb-8">
      {logos.map((logo, index) => (
        <img
          key={index}
          src={logo.src}
          onClick={() => window.open(logo.link, "_blank")}
          alt={logo.alt || `Logo ${index + 1}`}
          loading="eager"
          className="w-24 sm:w-32 md:w-40 h-auto rounded-lg bg-white p-2 hover:shadow-lg cursor-pointer transition-shadow duration-200"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      ))}
    </div>
  );


  const logos = [
    { src: Logo, alt: "Main Logo", link: "https://www.retinaspecialty.com/" },
    { src: segLogo, alt: "Secondary Logo", link: "https://southerneyegroup.com/" },
  ];

  const handleSendCode = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${config.API_BASE_URL}/send_otp`, // Replace with your Lambda endpoint
        { token: config.token }
      );

      if (response.status !== 200) {
        // Token invalid → stay on page
        navigate("/unauth", { state: { openedViaButton: false } });
      } else {
        // Token valid → show OTP input
        setShowOTP(true);
        setError("");
      }
    } catch (error) {
      if (error.response?.status === 400) {
        setError(error.response.data?.message || "Invalid token. Please try again.");
      } else if (error.response?.status === 401) {
        setError("Your session has expired. Please request a new code.");
      } else if (!error.response) {
        setError("Network error. Please check your connection and try again.");
      } else {
        navigate("/unauth", { state: { openedViaButton: false } });
      }
    } finally {
      setIsLoading(false);
    }
  }


  const validateDOB = (dob) => {

    if (dob.length !== 10) {
      return "Please enter full date (MM/DD/YYYY).";
    }

    const [month, day, year] = dob.split("/").map(Number);


    if (!month || !day || !year) {
      return "Invalid date format.";
    }

    if (month < 1 || month > 12) {
      return "Invalid month.";
    }

    if (day < 1 || day > 31) {
      return "Invalid day.";
    }

    if (year < 1900 || year > new Date().getFullYear()) {
      return "Invalid year.";
    }

    // ✅ iOS-safe date creation
    const parsedDate = new Date(year, month - 1, day);


    if (
      parsedDate.getFullYear() !== year ||
      parsedDate.getMonth() !== month - 1 ||
      parsedDate.getDate() !== day
    ) {
      return "Invalid calendar date.";
    }

    return false;
  };


  const handleLogin = async () => {
    // Blur active input to close keyboard and allow click
    if (document.activeElement) document.activeElement.blur();

    try {
      const dobError = validateDOB(dob);

      if (dobError) {
        setError(dobError);
        return;
      }
      setIsLoading(true);
      const response = await axios.post(
        `${config.API_BASE_URL}/verify_otp`,
        { token: config.token, otp: otp.join(""), dob }
      );

      if (response.status !== 200) {
        navigate("/unauth", { state: { openedViaButton: false } });
      } else {
        config.accessToken = response.data.auth_token;
        navigate("/process");
      }
    } catch (error) {
      if (error.response?.status === 400) {
        setError(error.response.data?.message || "Invalid OTP or Date of Birth. Please try again.");
      } else if (error.response?.status === 401) {
        setError("Authentication failed. Please request a new code.");
      } else if (!error.response) {
        setError("Network error. Please check your connection and try again.");
      } else {
        navigate("/unauth", { state: { openedViaButton: false } });
      }
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="relative min-h-screen w-full max-w-full overflow-hidden text-gray-900">
      {isLoading && <NextBtnLoader />}
      <Background />
      <div className="flex flex-col items-center justify-center gap-6 py-6 px-4 md:px-12 w-full max-w-full">

        {/* LEFT SIDE: Form */}
        <div className="flex flex-col items-center text-center p-4 w-full md:w-1/2 z-10 bg-white rounded-2xl">
          <LogoGroup logos={logos} />
          <img
            src={Paper}
            className="w-24 sm:w-32 md:w-48 lg:w-56 h-auto mb-4 mx-auto"
            alt="Form"
          />


          <h1 className="text-2xl md:text-4xl font-extrabold mb-4 text-black">
            Patient Intake Forms
          </h1>

          {!showOTP ? (
            <button
              onClick={handleSendCode}
              disabled={isLoading}
              className={`bg-blue-500 text-white py-3 px-6 rounded-2xl shadow-lg w-full max-w-xs
          hover:bg-blue-600 transition-all duration-300 text-sm flex items-center justify-center gap-2
          ${isLoading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"} transition-colors`}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Connecting...</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <i className="fa-regular fa-paper-plane"></i>
                  <span>Send Code</span>
                </div>
              )}
            </button>
          ) : (
            <div className="flex flex-col items-center w-full mt-4">
              {/* OTP Fields */}
              <label className="mb-2 text-sm font-medium text-gray-700">Enter OTP</label>
              <div className="flex justify-center gap-2 mb-4">
                {otp.map((digit, index) => (
                  <input
                    key={`otp-${index}`}
                    id={`otp-${index}`}
                    type="tel"
                    maxLength="1"
                    inputMode="numeric"
                    ref={(el) => {
                      if (el) inputRefs.current[index] = el;
                    }}
                    value={digit}
                    onChange={(e) => handleOTPChange(e.target.value, index)}
                    onKeyDown={(e) => handleOTPKeyDown(e, index)}
                    onPaste={handleOTPPaste}
                    className="w-10 h-10 text-center text-xl font-semibold border border-blue-400 rounded-2xl focus:ring-2 focus:ring-blue-400 outline-none shadow-sm"
                  />
                ))}
              </div>

              {/* DOB - Segmented Input */}
              <div className="w-full max-w-xs mb-4">
                <label className="mb-3 block text-sm font-medium text-gray-700">Enter Date of Birth</label>
                <div className="flex gap-2 items-center w-full justify-center">
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="MM"
                    maxLength="2"
                    value={dob.split("/")?.[0] || ""}
                    onChange={(e) => {
                      const month = e.target.value;
                      if (!/^\d{0,2}$/.test(month)) return;
                      const [, day = "", year = ""] = dob.split("/");
                      setDob(month ? `${month}/${day}/${year}`.replace(/\/+$/, "") : month);
                      if (month.length === 2) e.target.nextElementSibling?.nextElementSibling?.focus();
                    }}
                    onBlur={(e) => {
                      const month = e.target.value;
                      if (month && month.length === 1) {
                        const [, day = "", year = ""] = dob.split("/");
                        setDob(`${month.padStart(2, "0")}/${day}/${year}`.replace(/\/+$/, ""));
                      }
                    }}
                    disabled={isLoading}
                    className={`w-12 text-center border-2 rounded-lg px-2 py-2 text-base font-medium
                      ${error ? "border-red-400" : "border-blue-400"} focus:outline-none focus:ring-2 focus:ring-blue-300`}
                  />
                  <span className="text-gray-400 font-bold">/</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="DD"
                    maxLength="2"
                    value={dob.split("/")?.[1] || ""}
                    onChange={(e) => {
                      const day = e.target.value;
                      if (!/^\d{0,2}$/.test(day)) return;
                      const [month = "", , year = ""] = dob.split("/");
                      setDob(day ? `${month}/${day}/${year}`.replace(/\/+$/, "") : dob.split("/")[0] + "/" + day);
                      if (day.length === 2) e.target.nextElementSibling?.nextElementSibling?.focus();
                    }}
                    onBlur={(e) => {
                      const day = e.target.value;
                      if (day && day.length === 1) {
                        const [month = "", , year = ""] = dob.split("/");
                        setDob(`${month}/${day.padStart(2, "0")}/${year}`.replace(/\/+$/, ""));
                      }
                    }}
                    disabled={isLoading}
                    className={`w-12 text-center border-2 rounded-lg px-2 py-2 text-base font-medium
                      ${error ? "border-red-400" : "border-blue-400"} focus:outline-none focus:ring-2 focus:ring-blue-300`}
                  />
                  <span className="text-gray-400 font-bold">/</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="YYYY"
                    maxLength="4"
                    value={dob.split("/")?.[2] || ""}
                    onChange={(e) => {
                      const year = e.target.value;
                      if (!/^\d{0,4}$/.test(year)) return;
                      const [month = "", day = ""] = dob.split("/");
                      setDob(year ? `${month}/${day}/${year}` : dob.substring(0, dob.lastIndexOf("/") + 1) + year);
                    }}
                    disabled={isLoading}
                    className={`w-16 text-center border-2 rounded-lg px-2 py-2 text-base font-medium
                      ${error ? "border-red-400" : "border-blue-400"} focus:outline-none focus:ring-2 focus:ring-blue-300`}
                  />
                </div>
                <p className={`text-red-500 text-sm mt-2 h-5 ${error ? "" : "invisible"}`}>{error}</p>
              </div>

              {/* Login Button */}
              <button
                onClick={handleLogin}
                disabled={isLoading || !!validateDOB(dob)}
                className={`bg-blue-500 text-white py-3 px-6 rounded-2xl shadow-lg w-full max-w-xs
                hover:bg-blue-600 flex items-center justify-center gap-2 text-sm transition-all
                ${(isLoading || !!validateDOB(dob)) ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Connecting...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <i className="fa fa-sign-in"></i>
                    <span>Access Form</span>
                  </div>
                )}
              </button>


            </div>
          )}

          <p className="text-sm md:text-base mt-6 text-gray-500">Powered By Clear Healthcare</p>
        </div>

        {/* RIGHT SIDE: Market */}
        <div className="w-full md:w-1/2 flex justify-center mt-6 md:mt-0">
          <Market />
        </div>

      </div>

    </div>

  );
};

export default LoginPage;
